import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { VehicleCard } from "@/components/VehicleCard";
import { Reveal } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Loader2, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=1600";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brand: "tous", fuel: "tous", transmission: "tous", max_price: "tous" });

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.brand !== "tous") params.brand = filters.brand;
    if (filters.fuel !== "tous") params.fuel = filters.fuel;
    if (filters.transmission !== "tous") params.transmission = filters.transmission;
    if (filters.max_price !== "tous") params.max_price = Number(filters.max_price);
    api.get("/vehicles", { params }).then((r) => setVehicles(r.data)).catch(() => toast.error("Impossible de charger les véhicules. Réessayez.")).finally(() => setLoading(false));
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const FilterSelect = ({ k, placeholder, options }) => (
    <Select value={filters[k]} onValueChange={(v) => set(k, v)}>
      <SelectTrigger data-testid={`filter-${k}`} className="w-auto bg-[#121212] border-white/15 text-white focus:ring-[#C5A059] min-w-[160px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="tous">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="vehicles-page">
      <PageHero
        overline="Nos véhicules"
        title="Découvrez nos véhicules disponibles"
        subtitle="Retrouvez notre sélection de véhicules à la vente. Vous ne trouvez pas votre bonheur ? Utilisez notre recherche personnalisée."
        image={HERO}
      />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <span className="flex items-center gap-2 text-sm text-neutral-400"><SlidersHorizontal className="h-4 w-4 text-[#C5A059]" /> Filtrer :</span>
          <FilterSelect k="brand" placeholder="Toutes marques" options={brands.map((b) => ({ value: b, label: b }))} />
          <FilterSelect k="fuel" placeholder="Énergie" options={[{ value: "Essence", label: "Essence" }, { value: "Diesel", label: "Diesel" }, { value: "Hybride", label: "Hybride" }, { value: "Électrique", label: "Électrique" }]} />
          <FilterSelect k="transmission" placeholder="Boîte" options={[{ value: "Automatique", label: "Automatique" }, { value: "Manuelle", label: "Manuelle" }]} />
          <FilterSelect k="max_price" placeholder="Budget max" options={[{ value: "20000", label: "≤ 20 000 €" }, { value: "40000", label: "≤ 40 000 €" }, { value: "60000", label: "≤ 60 000 €" }, { value: "100000", label: "≤ 100 000 €" }]} />
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
        ) : vehicles.length === 0 ? (
          <div data-testid="no-vehicles" className="text-center py-20 border border-white/10 rounded-sm bg-[#121212]">
            <Search className="h-10 w-10 text-[#C5A059] mx-auto mb-4" />
            <h3 className="font-display text-2xl text-white">Aucun véhicule ne correspond</h3>
            <p className="text-neutral-400 mt-2">Laissez-nous chercher pour vous grâce à la recherche personnalisée.</p>
            <Link to="/recherche"><Button className="mt-6 bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">Recherche personnalisée</Button></Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v, i) => <Reveal key={v.id} delay={(i % 3) * 0.08}><VehicleCard vehicle={v} /></Reveal>)}
          </div>
        )}

        <Reveal>
          <div className="mt-16 rounded-sm border border-white/10 bg-[#0f0f0f] p-8 text-center">
            <h3 className="font-display text-2xl text-white">Vous ne trouvez pas votre bonheur ?</h3>
            <p className="text-neutral-400 mt-2">Votre voiture idéale existe peut-être déjà. Laissez-nous la trouver.</p>
            <Link to="/recherche"><Button className="mt-6 bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm px-8">Déposer une recherche personnalisée</Button></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
